<script setup lang="ts">
import IconArrowRight from '@rendora/design/assets/arrow.svg?component';
import type { HTMLAttributes } from 'vue';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
// 组件 Props 定义
interface Props {
  // 受控展开状态，优先级高于 defaultOpen
  modelValue?: boolean;
  // 非受控默认是否展开
  defaultOpen?: boolean;
  // 是否禁用
  disabled?: boolean;
  // 标题（可通过 slot 自定义）
  title?: string;
  // 自定义类名/样式
  class?: HTMLAttributes['class'];
  style?: HTMLAttributes['style'];
}

// Emits 定义
interface Emits {
  (e: 'update:modelValue', value: boolean): void;
  (e: 'toggle', value: boolean): void;
}

const props = withDefaults(defineProps<Props>(), {
  defaultOpen: false,
  disabled: false,
});

const emit = defineEmits<Emits>();

// 本地状态（用于非受控）
const uncontrolledOpen = ref<boolean>(props.defaultOpen);

// 是否为受控模式
const isControlled = computed(() => typeof props.modelValue === 'boolean');

// 当前展开状态（受控优先）
const isOpen = computed<boolean>({
  get: () => (isControlled.value ? !!props.modelValue : uncontrolledOpen.value),
  set: (value: boolean) => {
    if (props.disabled) return;
    if (isControlled.value) {
      emit('update:modelValue', value);
    } else {
      uncontrolledOpen.value = value;
      emit('update:modelValue', value);
    }
    emit('toggle', value);
  },
});

// 内容区域高度控制，确保有平滑过渡
const contentRef = ref<HTMLDivElement | null>(null);
const contentMaxHeight = ref<string>('0px');
let resizeObserver: ResizeObserver | null = null;

function setMaxHeightToContent() {
  const el = contentRef.value;
  if (!el) return;
  contentMaxHeight.value = `${el.scrollHeight}px`;
}

function openPanel() {
  const el = contentRef.value;
  if (!el) return;
  // 先从 0 设到内容高度，触发过渡
  setMaxHeightToContent();
}

function closePanel() {
  const el = contentRef.value;
  if (!el) return;
  // 若当前为 auto（none），先锁定当前高度再置 0
  if (contentMaxHeight.value === 'none') {
    contentMaxHeight.value = `${el.scrollHeight}px`;
    // 下一帧置 0，触发收起动画
    requestAnimationFrame(() => {
      contentMaxHeight.value = '0px';
    });
  } else {
    contentMaxHeight.value = '0px';
  }
}

function handleTransitionEnd() {
  // 展开完成后将 maxHeight 置为 none，允许内部内容后续自然扩展
  if (isOpen.value) {
    contentMaxHeight.value = 'none';
  }
}

function toggle() {
  if (props.disabled) return;
  // 从视觉角度先准备动画再切换状态
  if (isOpen.value) {
    closePanel();
  } else {
    // 若从收起到展开，先设置为具体高度
    openPanel();
  }
  isOpen.value = !isOpen.value;
}

onMounted(() => {
  // 初始高度
  if (isOpen.value) {
    // 初始展开：直接设为 none，避免初次闪烁
    contentMaxHeight.value = 'none';
  } else {
    contentMaxHeight.value = '0px';
  }

  // 监听内容变化，展开状态下动态更新高度
  const el = contentRef.value;
  if (el && 'ResizeObserver' in window) {
    resizeObserver = new ResizeObserver(() => {
      if (!isOpen.value) return;
      // 过渡结束后使用 none，不需要更新；只有在展开动画期间或从 none 切换到具体高度时更新
      if (contentMaxHeight.value !== 'none') {
        setMaxHeightToContent();
      }
    });
    resizeObserver.observe(el);
  }
});

onBeforeUnmount(() => {
  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }
});

watch(isOpen, val => {
  // 展开/收起时重新测量
  if (val) {
    // 从 0 到内容高度
    requestAnimationFrame(openPanel);
  } else {
    // 从 auto/固定高度到 0
    closePanel();
  }
});

// 头部可访问性属性
const headerTabIndex = computed(() => (props.disabled ? -1 : 0));

function onHeaderKeydown(e: KeyboardEvent) {
  if (props.disabled) return;
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    toggle();
  }
}
</script>

<template>
  <div
    class="r-collapse"
    :class="[disabled && 'r-collapse--disabled', isOpen && 'r-collapse--open', props.class]"
    :style="props.style"
  >
    <div
      class="r-collapse__header"
      role="button"
      :aria-expanded="isOpen"
      :tabindex="headerTabIndex"
      @click="toggle"
      @keydown="onHeaderKeydown"
    >
      <div class="r-collapse__title">
        <slot name="title">{{ title }}</slot>
      </div>
      <div class="r-collapse__extra">
        <slot name="extra"></slot>
      </div>
      <!-- <div class="r-collapse__arrow" aria-hidden="true"> -->
      <IconArrowRight class="r-collapse__arrow" />
      <!-- </div> -->
    </div>

    <div
      ref="contentRef"
      class="r-collapse__content"
      :style="{ maxHeight: contentMaxHeight }"
      @transitionend="handleTransitionEnd"
    >
      <div class="r-collapse__content-inner">
        <slot />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.r-collapse {
  background-color: var(--x-back-1);
  border-top: 1px solid var(--x-back-4);
  &.r-collapse--disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.r-collapse__header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 22px 20px;
  line-height: 20px;
  font-size: 14px;
  cursor: pointer;
  user-select: none;
  color: var(--x-text-1);
}

// 简单箭头，避免外部依赖
.r-collapse__arrow {
  color: var(--x-text-1);
  width: 18px;
  height: 18px;
  transition: transform 0.2s ease;
  transform: rotate(90deg);
}

.r-collapse--open .r-collapse__arrow {
  transform: rotate(270deg);
}

.r-collapse__title {
  flex: 1;
  min-width: 0;
  font-size: 14px;
  font-weight: var(--x-weight-medium);
  margin-right: 20px; // 标题与右侧区域间距
}

.r-collapse__extra {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.r-collapse__content {
  overflow: hidden;
  transition: max-height 0.2s ease;
}

.r-collapse__content-inner {
  padding: 0px 20px 20px 20px; // 与标题区保持 20px 间距
  color: var(--x-text-5);
  font-size: 12px;
  line-height: 21px;
}
</style>
